import { Banner, PrimaryButton } from "@brexhq/component-library";
import { VStack } from "@brexhq/metal-layout";
import styled from "@emotion/styled";
import { margin, padding, readableColor, border } from "polished";
import React from "react";

import { filterByVisibilityRules } from "@brexhq/next/src/components/building-blocks/MarketoForm/utils";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SpacingValues } from "@brexhq/shared/types/SpacingScale";

import { getModuleThemeColors } from "../../../pageBuilder/ModuleLayout";
import { createInlineSSUButtonStyles } from "../../../shared/styles/Button.elements";
import {
  FormStatus,
  DataType,
  FieldState,
  SetFields,
  SubmitError,
} from "../types";

import { FormField } from "./FormField";

export const FormSubmit = styled.button(createInlineSSUButtonStyles());

const Wrapper = styled.div<{
  showBackground?: boolean;
  backgroundColor?: string;
  horizontalSpacing?: SpacingValues;
  textColor?: string;
}>(
  ({
    theme,
    showBackground,
    backgroundColor,
    horizontalSpacing,
    textColor,
  }) => {
    const verticalPadding = showBackground ? theme.spacing[40] : 0;
    const horizontalPadding =
      showBackground && horizontalSpacing
        ? theme.spacing.LRG[horizontalSpacing]
        : verticalPadding;

    return {
      borderRadius: theme.borders.radius.default,
      ...padding(verticalPadding, horizontalPadding),
      background: showBackground
        ? backgroundColor ?? theme.colors.background.secondary
        : "transparent",

      h4: {
        color:
          showBackground && backgroundColor
            ? readableColor(
                backgroundColor ?? theme.colors.background.secondary,
              )
            : textColor,
      },

      form: {
        label: {
          color:
            showBackground && backgroundColor
              ? readableColor(
                  backgroundColor ?? theme.colors.background.secondary,
                )
              : textColor,
        },
      },
    };
  },
);

const StyledForm = styled.form({
  display: "flex",
  flexDirection: "column",
});

const ActionContainer = styled.div({
  width: "100%",
});

const ErrorContainer = styled.div(({ theme }) => ({
  width: "100%",
  ...margin(theme.spacing[32], 0, 0, 0), // dashboard theme spacing
}));

const InlineEmailFormWrapper = styled.div(({ theme }) => ({
  ...border(theme.borders.width.default, "solid"),
  alignSelf: "center",
  overflow: "hidden",
  borderColor: theme.colors.border.default,
  "&:focus-within": {
    borderColor: theme.colors.border.focused,
  },
  display: "inline-flex",
  width: "100%",
  padding: theme.spacing[8],
  paddingLeft: theme.spacing[16],
  [theme.breakpoints.SML]: {
    padding: "5px",
    paddingLeft: theme.spacing[8],
    "& input": {
      minWidth: "auto",
    },
  },
  justifyContent: "space-between",
  borderRadius: theme.borders.radius.default,
  flexDirection: "row",
  alignItems: "center",
  background: theme.colors.background.primary,
}));

const DesktopWrapper = styled.div(({ theme }) => ({
  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

const MobileWrapper = styled.div(({ theme }) => ({
  display: "none",
  [theme.breakpoints.SML]: {
    display: "flex",
  },
}));

interface Props {
  fieldData: FieldState;
  setFields: SetFields;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitError: SubmitError;
  submitStatus: FormStatus;
  showBackground?: boolean;
  backgroundColor?: string;
  submitButtonLabel: string;
  formId: number;
  anchor?: string;
  topSpacing: SpacingValues;
  horizontalSpacing?: SpacingValues;
  FormHeaderComponent?: React.ReactNode;
  FormFooterComponent?: React.ReactNode;
  theme?: ModuleThemeType;
  hideNonEmailFields?: boolean | null;
  isInlineEmail?: boolean | null;
  useArrowButtonOnMobile?: boolean | null;
}

enum nameFieldsKeys {
  firstName = "FirstName",
  lastName = "LastName",
}

enum emailFieldKey {
  email = "Email",
}

export const Form: React.FC<Props> = ({
  fieldData,
  onSubmit,
  submitError,
  showBackground,
  backgroundColor,
  submitStatus,
  submitButtonLabel,
  setFields,
  formId,
  anchor,
  topSpacing,
  horizontalSpacing,
  FormHeaderComponent,
  FormFooterComponent,
  theme = "PrimaryWhite",
  hideNonEmailFields,
  isInlineEmail,
  useArrowButtonOnMobile,
}) => {
  const moduleThemeColors = getModuleThemeColors(theme);

  const anchorProps = anchor ? { id: anchor } : {};

  const submitButton = React.useMemo(() => {
    const button = (
      <ActionContainer>
        <Container
          alignItems="flex-start"
          justifyContent="flex-end"
          direction="row"
          padding={{
            LRG: ["sml", 0, 0, 0],
            MED: ["sml", 0, 0, 0],
            SML: ["sml", 0, 0, 0],
          }}
        >
          <VStack flex={1}>
            <PrimaryButton
              aria-label="Submit form"
              type="submit"
              loading={submitStatus === FormStatus.SUBMITTING}
            >
              {submitButtonLabel || "Submit"}
            </PrimaryButton>
          </VStack>
        </Container>
      </ActionContainer>
    );

    const inlineButton = (
      <Container
        alignItems="flex-start"
        justifyContent="flex-end"
        direction="row"
      >
        <VStack flex={1}>
          {useArrowButtonOnMobile ? (
            <>
              <DesktopWrapper>
                <PrimaryButton
                  aria-label="Submit form"
                  type="submit"
                  loading={submitStatus === FormStatus.SUBMITTING}
                >
                  {submitButtonLabel || "Submit"}
                </PrimaryButton>
              </DesktopWrapper>
              <MobileWrapper>
                <FormSubmit type="submit">{"->"}</FormSubmit>
              </MobileWrapper>
            </>
          ) : (
            <PrimaryButton
              aria-label="Submit form"
              type="submit"
              loading={submitStatus === FormStatus.SUBMITTING}
            >
              {submitButtonLabel || "Submit"}
            </PrimaryButton>
          )}
        </VStack>
      </Container>
    );

    if (isInlineEmail) {
      return inlineButton;
    }

    return button;
  }, [submitButtonLabel, submitStatus, isInlineEmail, useArrowButtonOnMobile]);

  const nameFields = React.useMemo(() => {
    const nameFields = Object.keys(fieldData).filter((field) => {
      const isVisible = filterByVisibilityRules(fieldData[field], fieldData);
      return (
        (fieldData[field].name === nameFieldsKeys.firstName ||
          fieldData[field].name === nameFieldsKeys.lastName) &&
        isVisible
      );
    });

    if (nameFields.length === 0) {
      return null;
    }

    return (
      <Container
        display="flex"
        direction={{
          LRG: "row",
          SML: "column",
        }}
        gap={{
          LRG: "sml",
          MED: "sml",
          SML: 0,
        }}
      >
        {nameFields.map((key) => {
          const field = fieldData[key];
          const isHidden =
            !!hideNonEmailFields && field.dataType !== DataType.EMAIL;
          return (
            <Container
              padding={{
                LRG: [field.dataType !== DataType.HIDDEN ? "sml" : 0, 0, 0, 0],
              }}
              flex={1}
              key={field.name}
              style={{
                display: isHidden ? "none" : "block",
              }}
            >
              <FormField
                field={field}
                setFields={setFields}
                submitAttempted={submitStatus === FormStatus.SUBMIT_ATTEMPTED}
                formId={formId}
              />
            </Container>
          );
        })}
      </Container>
    );
  }, [fieldData, setFields, submitStatus, formId, hideNonEmailFields]);

  const restFields = React.useMemo(() => {
    const restFields = Object.keys(fieldData).filter((field) => {
      const isVisible = filterByVisibilityRules(fieldData[field], fieldData);
      return (
        fieldData[field].name !== nameFieldsKeys.firstName &&
        fieldData[field].name !== nameFieldsKeys.lastName &&
        isVisible
      );
    });

    if (restFields.length === 0) {
      return null;
    }

    return restFields.map((key) => {
      const field = fieldData[key];
      const isHidden =
        !!hideNonEmailFields && field.dataType !== DataType.EMAIL;
      const isEmailField = field.name === emailFieldKey.email;

      if (isInlineEmail && isEmailField) {
        return (
          <InlineEmailFormWrapper key={field.name}>
            <FormField
              field={field}
              setFields={setFields}
              submitAttempted={submitStatus === FormStatus.SUBMIT_ATTEMPTED}
              formId={formId}
              isInlineEmail={isInlineEmail}
            />
            {submitButton}
          </InlineEmailFormWrapper>
        );
      }

      return (
        <Container
          key={field.name}
          padding={{
            LRG: [field.dataType !== DataType.HIDDEN ? "sml" : 0, 0, 0, 0],
          }}
          style={{
            display: isHidden ? "none" : "block",
          }}
        >
          <FormField
            key={field.name}
            field={field}
            setFields={setFields}
            submitAttempted={submitStatus === FormStatus.SUBMIT_ATTEMPTED}
            formId={formId}
          />
        </Container>
      );
    });
  }, [
    fieldData,
    setFields,
    submitStatus,
    formId,
    hideNonEmailFields,
    submitButton,
    isInlineEmail,
  ]);

  return (
    <Container {...anchorProps} width="100%" margin={[topSpacing, 0, 0]}>
      <Wrapper
        showBackground={showBackground}
        backgroundColor={
          backgroundColor ?? moduleThemeColors?.standard?.background
        }
        horizontalSpacing={horizontalSpacing}
        textColor={moduleThemeColors.standard.textPrimary}
      >
        {FormHeaderComponent && FormHeaderComponent}
        <StyledForm onSubmit={onSubmit} data-form-id={`form-${formId}`}>
          {nameFields}
          {restFields}
          <Container>
            {!isInlineEmail && submitButton}
            {submitError && (
              <ErrorContainer>
                <Banner variant="error" title={submitError.title}>
                  {submitError.message}
                </Banner>
              </ErrorContainer>
            )}
          </Container>
        </StyledForm>
        {FormFooterComponent && (
          <Container
            padding={{
              LRG: ["xlrg", 0, 0, 0],
              MED: ["xlrg", 0, 0, 0],
              SML: ["med", 0, 0, 0],
            }}
          >
            {FormFooterComponent}
          </Container>
        )}
      </Wrapper>
    </Container>
  );
};
